import React from "react";
// eslint-disable-next-line no-restricted-imports -- TODO: update to `next/navigation` before using this component in App Router
import { useRouter } from "next/router";

import ProductLandingPageFAQs from "@/__test__/fixtures/ProductLandingPageFAQs";

import { getProductMarketplaceCategories } from "@/api/requests/getProductMarketplaceCategories";
import getSponsoredArticles, {
  SponsoredArticle,
} from "@/api/requests/getSponsoredArticles";
import publicConfig from "@/config/public";
import { SEO } from "@/custom-types/Directus";
import { ProductMarketplaceCategory } from "@/custom-types/Product";
import { createGetServerSideProps } from "@/utils/createGetServerSideProps";
import { getCountryCodeHeader } from "@/utils/requestContext";

import FAQs from "@/components/FAQ";
import ProductsCoinList from "@/components/products/CoinList/ProductsCoinList";
import { FeaturedProducts } from "@/components/products/FeaturedProducts";
import { HeroBanner } from "@/components/products/HeroBanner";
import { LazyLoadProductsCarouselWithData } from "@/components/products/LazyLoadProductsCarouselWithData";
import { hasIosParam } from "@/components/products/lib/paramCheck";
import { ProductBreadcrumbs } from "@/components/products/ProductCategoryBreadcrumbs";
import { ProductCategoryMetaTags } from "@/components/products/ProductCategoryMetaTags";
import { ProductCategoryNavigation } from "@/components/products/ProductCategoryNavigation";
import ProductsAd from "@/components/products/ProductsAd";
import { ProductsCarouselWithData } from "@/components/products/ProductsCarouselWithData";
import ProductsCopyBlocks from "@/components/products/ProductsCopyBlocks";
import ProductsHeroAd from "@/components/products/ProductsHeroAd";
import { SponsoredArticlesCarousel } from "@/components/products/SponsoredArticlesCarousel";
import SubcategoryCarousel from "@/components/products/SubcategoryCarousel";
import ProductSubcategoryLinks from "@/components/ProductSubcategoryLinks";

import getSeo from "../../api/requests/directus/getSeo";

export const CAROUSEL_ROUTE_MAP = {
  cartridges: "/products/concentrates/cartridges",
  concentrates: "/products/concentrates",
  edibles: "/products/edibles",
  flowers: "/products/cannabis/flower",
  prerolls: "/products/cannabis/prerolls",
  vapes: "/products/vaping",
} as const;

const DEFAULT_H1 = "THC products, CBD, weed, & more";
const DEFAULT_DESCRIPTION =
  "Whether you are just getting interested in weed, THC, CBD, and Delta 8 products or if you are a seasoned stoner with a favorite strain; find the perfect product that fits your needs.";
const DEFAULT_META_TITLE =
  "THC Products, CBD, Weed, & More Online and Near You";
const DEFAULT_META_DESCRIPTION =
  "Whether you are just getting interested in weed, THC, CBD, and Delta 8 products or if you are a seasoned stoner with a favorite strain; Leafly helps you find the perfect product that fits your needs.";
const DEFAULT_IMAGE = `${publicConfig.imgix.publicUrl}/products/category-heroes/product-heroes-v4-1920x500-all-products-1x.jpg`;

type ProductIndexProps = {
  categories: ProductMarketplaceCategory[];
  articles: SponsoredArticle[];
  isUsCountryCode: boolean;
  seo: SEO | null;
};

const ProductIndex: React.FC<ProductIndexProps> = ({
  categories,
  articles,
  isUsCountryCode,
  seo,
}) => {
  const router = useRouter();
  const isIos = hasIosParam(router);

  const getSubcategorySlugsByCategory = (category: string) => {
    return categories
      .filter((cat) => cat.name === category)
      .flatMap((cat) => cat.subcategories.map((cat) => cat.slug));
  };
  return (
    <>
      <ProductCategoryMetaTags
        title={seo?.metaTitle || DEFAULT_META_TITLE}
        description={seo?.metaDescription || DEFAULT_META_DESCRIPTION}
        image={seo?.heroImage || DEFAULT_IMAGE}
        canonicalOverride={seo?.canonicalOverride || undefined}
      />

      <ProductCategoryNavigation categories={categories} />

      <HeroBanner
        imageSrc={seo?.heroImage || DEFAULT_IMAGE}
        title={seo?.h1 || DEFAULT_H1}
        subtitle={seo?.description || DEFAULT_DESCRIPTION}
        useUpdatedHeroStyling={isUsCountryCode}
      />

      <ProductsHeroAd useUpdatedHeroStyling={isUsCountryCode} />

      {isUsCountryCode && (
        <ProductsCoinList className="container" title="Browse by category" />
      )}

      <FeaturedProducts
        className="my-xl md:my-section container"
        titleOverride={seo?.h2 || undefined}
      />
      <div className="container">
        <ProductsCarouselWithData
          carouselPath={CAROUSEL_ROUTE_MAP.flowers}
          header="Flower"
          filter={{ subcategory: "flower" }}
          {...(isUsCountryCode
            ? {
                carouselClassName: "mt-xl md:mt-section mb-lg md:mb-sm",
                seeMoreColor: "default",
                seeMoreText: "view all flower",
              }
            : {})}
        />
        {isUsCountryCode ? (
          <ProductSubcategoryLinks category="cannabis" subcategory="flower" />
        ) : null}

        <ProductsCarouselWithData
          carouselPath={CAROUSEL_ROUTE_MAP.edibles}
          carouselClassName={isUsCountryCode ? "mb-md mt-xl" : ""}
          header="Edibles"
          filter={{ category: "edibles" }}
          {...(isUsCountryCode
            ? {
                seeMoreColor: "default",
                seeMoreText: "view all edibles",
              }
            : {})}
        />
        {isUsCountryCode && (
          <SubcategoryCarousel
            categorySlug="edibles"
            className="text-center"
            subcategories={getSubcategorySlugsByCategory("Edibles")}
            headingClassName="uppercase text-sm font-extrabold"
            title={`Browse edibles by category`}
          />
        )}

        <ProductsAd
          className="my-xxl md:my-section gam-rectangular-fixed-height"
          sizes={{ desktop: [728, 90], mobile: [320, 50] }}
          unit="product_upper"
        />

        {!isIos && (
          <LazyLoadProductsCarouselWithData
            height={350}
            offset={100}
            carouselPath={CAROUSEL_ROUTE_MAP.cartridges}
            header="Cartridges"
            filter={{ subcategory: "cartridges" }}
            {...(isUsCountryCode
              ? {
                  carouselClassName: "mt-xl md:mt-section mb-lg md:mb-sm",
                  seeMoreColor: "default",
                  seeMoreText: "view all cartridges",
                }
              : {})}
          />
        )}
        {isUsCountryCode ? (
          <ProductSubcategoryLinks
            category="concentrates"
            subcategory="cartridges"
          />
        ) : null}

        <LazyLoadProductsCarouselWithData
          height={350}
          offset={100}
          carouselPath={CAROUSEL_ROUTE_MAP.prerolls}
          header="Pre-rolls"
          filter={{ subcategory: "prerolls" }}
          {...(isUsCountryCode
            ? {
                carouselClassName: "mt-xl md:mt-section mb-lg md:mb-sm",
                seeMoreColor: "default",
                seeMoreText: "view all pre-rolls",
              }
            : {})}
        />
        {isUsCountryCode ? (
          <ProductSubcategoryLinks category="cannabis" subcategory="prerolls" />
        ) : null}

        <LazyLoadProductsCarouselWithData
          height={350}
          offset={100}
          carouselPath={CAROUSEL_ROUTE_MAP.concentrates}
          carouselClassName={isUsCountryCode ? "mb-md mt-xl" : ""}
          header="Concentrates"
          filter={{ category: "concentrates" }}
          {...(isUsCountryCode
            ? {
                seeMoreColor: "default",
                seeMoreText: "view all concentrates",
              }
            : {})}
        />
        {isUsCountryCode && (
          <SubcategoryCarousel
            categorySlug="concentrates"
            className="text-center"
            subcategories={getSubcategorySlugsByCategory("Concentrates")}
            headingClassName="uppercase text-sm font-extrabold"
            title={`Browse concentrates by category`}
          />
        )}

        <ProductsAd
          className="my-xxl md:my-section gam-rectangular-min-height"
          sizes={{
            desktop: [728, 90],
            mobile: [
              [320, 50],
              [300, 250],
            ],
          }}
          unit="product_mid"
        />

        {!isIos && (
          <LazyLoadProductsCarouselWithData
            height={350}
            offset={100}
            carouselPath={CAROUSEL_ROUTE_MAP.vapes}
            carouselClassName={isUsCountryCode ? "mb-md mt-xl" : ""}
            header="Vaping"
            filter={{ category: "vaping" }}
            {...(isUsCountryCode
              ? {
                  seeMoreColor: "default",
                  seeMoreText: "view all vaping",
                }
              : {})}
          />
        )}

        {!isIos && isUsCountryCode && (
          <SubcategoryCarousel
            categorySlug="vaping"
            className="text-center"
            subcategories={getSubcategorySlugsByCategory("Vaping")}
            headingClassName="uppercase text-sm font-extrabold"
            title={`Browse vaping by category`}
          />
        )}
      </div>

      <SponsoredArticlesCarousel
        className="my-xl md:my-section container"
        initialArticles={articles}
        sponsorId={0}
        sponsorType="category"
      />

      <ProductsCopyBlocks
        className="container my-xl md:my-section"
        blocks={seo?.copyBlocks}
      />

      <FAQs
        title={
          seo?.faqTitle ||
          "Frequently asked questions about buying cannabis products"
        }
        data={seo?.faqs || ProductLandingPageFAQs}
        initialShowPanel={true}
        className="my-xl md:my-section"
      />

      <ProductsAd
        className="my-xxl md:my-section gam-rectangular-min-height"
        sizes={{
          desktop: [728, 90],
          mobile: [
            [320, 50],
            [300, 250],
          ],
        }}
        unit="product_lower"
      />

      {isUsCountryCode && (
        <div className="container">
          <ProductBreadcrumbs />
        </div>
      )}
    </>
  );
};

export const getServerSideProps = createGetServerSideProps<ProductIndexProps>(
  async (context) => {
    const countryCode = getCountryCodeHeader(context.req.headers);
    const isUsCountryCode = countryCode === "US";

    const [{ categories }, articles, seo] = await Promise.all([
      getProductMarketplaceCategories(countryCode),
      getSponsoredArticles({ id: 0, sponsorType: "category" }),
      getSeo(context.resolvedUrl, countryCode),
    ]);

    return {
      props: {
        articles,
        categories,
        isUsCountryCode,
        seo,
      },
    };
  },
);

export default ProductIndex;
